
import { Component, OnInit, inject, input, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Dialog } from '@capacitor/dialog';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AppCommonModule } from 'src/app/common.module';
import { MainDialogComponent } from 'src/app/components/main-dialog/main-dialog.component';
import { ContractorOffer } from 'src/app/models/contractor-offer.model';
import { Contractor } from 'src/app/models/contractor.model';
import { PartnerNote } from 'src/app/models/partner-note.model';
import { ContractorOffersService } from 'src/app/services/contractor-offers.service';
import { ContractorsService } from 'src/app/services/contractors.service';
import { PartnerNotesService } from 'src/app/services/partner-notes.service';

export interface PartnerInviteContractor {
  name: string;
  businessId: string;
  admins: { displayName: string; email: string }[];
  supervisors: { displayName: string; email: string }[];
  workers: { displayName: string; email: string }[];
}

@Component({
  selector: 'app-partner-subcontractor-dialog',
  templateUrl: './subcontractor.dialog.html',
  styleUrls: ['./subcontractor.dialog.scss'],
  imports: [
    AppCommonModule,
    MainDialogComponent,
  ],
})
export class PartnerSubcontractorDialogComponent implements OnInit {
  private contractorsService = inject(ContractorsService);
  private contractorOffersService = inject(ContractorOffersService);
  private formBuilder = inject(FormBuilder);
  private _modal = inject(ModalController);
  private partnerNotesService = inject(PartnerNotesService);
  private translate = inject(TranslateService);
  private offer: ContractorOffer;

  item = input<PartnerNote>();
  step = signal(1);
  formGroup: FormGroup;

  ngOnInit(): void {
    this.setupForm();
  }

  private async setupForm() {
    const item = this.item();
    if (!item) {
      return;
    }
    this.offer = await firstValueFrom(this.contractorOffersService.get(item.offer));

    this.formGroup = this.formBuilder.group({
      name: [item.target, Validators.required],
      businessId: [this.offer.businessId, Validators.required],
      admins: this.formBuilder.array([]),
      supervisors: this.formBuilder.array([]),
      workers: this.formBuilder.array([]),
    });
    for (let i = 0; i < this.offer.costs.adminQty; i++) {
      this.addItem('admins');
    }
    for (let i = 0; i < this.offer.costs.supervisorQty; i++) {
      this.addItem('supervisors');
    }
    for (let i = 0; i < this.offer.costs.workerQty; i++) {
      this.addItem('workers');
    }
  }

  getFields(field: string) {
    return this.formGroup.get(field) as FormArray;
  }

  addItem(field: string) {
    const item = this.formBuilder.group({
      displayName: [''],
      email: ['', [Validators.email]],
    });
    this.getFields(field).push(item);
  }

  deleteItem(index: number, field: string) {
    this.getFields(field).removeAt(index);
  }

  async save() {
    const item = this.formGroup.value as PartnerInviteContractor;
    const admins = item.admins.filter((it) => it.displayName.length > 0 && it.email.length > 0) ?? [];
    const supervisors = item.supervisors.filter((it) => it.displayName.length > 0 && it.email.length > 0) ?? [];
    const workers = item.workers.filter((it) => it.displayName.length > 0 && it.email.length > 0) ?? [];
    const contractor: Contractor = {
      name: item.name,
      admins,
      supervisors,
      workers,
      license: 'boost',
      prices: {
        admin: this.offer.costs.admin ?? 0,
        supervisor: this.offer.costs.supervisor ?? 0,
        worker: this.offer.costs.worker ?? 0,
      },
      licenses: {
        admin: this.offer.costs.adminQty ?? 0,
        supervisor: this.offer.costs.supervisorQty ?? 0,
        worker: this.offer.costs.workerQty ?? 0,
      },
      created: new Date(),
      licenseStart: new Date(),
      email: null,
      businessId: item.businessId,
      sites: [],
      clients: [],
      disabled: false,
    };
    const found = await firstValueFrom(this.contractorsService.hasContractor(contractor.name));
    if (found) {
      Dialog.alert({
        message: this.translate.instant('signup.found'),
      });
      return null;
    } else {
      const doc = await this.contractorsService.save(contractor);
      contractor.guid = doc.id;
      const partnerNote = this.item();
      this.partnerNotesService.updateOnly(partnerNote.guid, { contractor: doc.id });
      this.step.set(3);
      return contractor;
    }
  }

  dismiss(item?: PartnerInviteContractor) {
    this._modal.dismiss(item);
  }
}
